import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { fetchPublicPreview, type PublicPreview, type PublicPreviewCandidate } from "@/lib/employer-api";

export const Route = createFileRoute("/roles/$ref")({
  head: () => {
    const t = "People who applied for your role | CLOSDD";
    const d =
      "See the salespeople who applied for your role. Listen to their 60-second pitches, then log in to open the full applications.";
    return {
      meta: [
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "noindex" },
      ],
    };
  },
  component: RolePreview,
});

const btn =
  "inline-flex items-center justify-center rounded-full bg-purple-strong px-7 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-purple-mid";
const label = "text-[11px] font-bold uppercase tracking-[0.22em] text-ink/45";

function initialsOf(name: string) {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

function RolePreview() {
  const { ref } = Route.useParams();
  const [preview, setPreview] = useState<PublicPreview | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchPublicPreview(ref).then((found) => {
      if (!cancelled) {
        setPreview(found);
        setChecked(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [ref]);

  if (!checked) return <div className="min-h-screen bg-beige" />;

  if (!preview) {
    return (
      <div className="min-h-screen bg-beige">
        <main className="mx-auto w-full max-w-[720px] px-5 py-24 text-center">
          <h1 className="font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink">This link is not active.</h1>
          <p className="mt-4 text-[17px] text-ink/65">
            It may have already been used to create an account, or expired. Email info@closdd.com and we will help you.
          </p>
        </main>
      </div>
    );
  }

  const { job, candidates } = preview;
  const total = candidates.length;
  const claimHref = { to: "/employer-signup", search: { ref } } as const;

  return (
    <div className="min-h-screen bg-beige">
      <header className="sticky top-0 z-40 border-b border-ink/[0.06] bg-beige/85 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[1120px] items-center justify-between gap-6 px-5 py-4">
          <Link to="/" className="font-serif text-[22px] font-bold uppercase leading-none tracking-[0.04em] text-purple-strong">
            closdd
          </Link>
          <Link {...claimHref} className={btn}>
            Create free account
          </Link>
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-[1120px] gap-6 px-5 py-6">
        <section className="relative overflow-hidden rounded-[36px] bg-purple-strong px-6 py-12 sm:px-12 sm:py-16">
          <div className="pointer-events-none absolute -left-40 -top-40 size-[520px] rounded-full bg-[radial-gradient(circle,rgba(230,238,251,0.22),transparent_65%)] blur-2xl" />
          <div className="pointer-events-none absolute -bottom-52 left-1/3 size-[620px] rounded-full bg-[radial-gradient(circle,rgba(235,225,253,0.2),transparent_65%)] blur-2xl" />
          <div className="relative max-w-[860px]">
            <p className="mb-6 text-[12px] font-bold uppercase tracking-[0.28em] text-mint-soft">
              Private link for {job.company}
            </p>
            <h1 className="font-serif text-[32px] leading-[0.94] tracking-[-0.03em] text-white sm:text-[44px] lg:text-[64px]">
              {total > 0
                ? `${total} salesp${total === 1 ? "erson has" : "eople have"} already applied to your role.`
                : "Your role is live, and salespeople are applying."}
            </h1>
            <p className="mt-7 max-w-[620px] font-serif text-[19px] leading-[1.15] tracking-[-0.02em] text-white sm:text-[24px]">
              They didn&apos;t just send a CV. They pitched themselves.
            </p>
            <p className="mt-5 max-w-[560px] text-[16px] leading-[1.7] text-white/65">
              Create your free employer account to hear every 60-second pitch, read the voice assessments and see who
              is worth interviewing. Listening is always free.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link {...claimHref} className="inline-flex items-center rounded-full bg-white px-7 py-4 text-[13.5px] font-bold uppercase tracking-[0.1em] text-purple-strong transition-colors hover:bg-mint-soft">
                Create free account
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center rounded-full border border-white/30 px-7 py-4 text-[13.5px] font-bold uppercase tracking-[0.1em] text-white transition-colors hover:bg-white/10"
              >
                I already have one
              </Link>
            </div>
            <p className="mt-6 text-[12px] font-bold uppercase tracking-[0.2em] text-white/55">
              Free account · No card needed · Takes 30 seconds
            </p>
          </div>
        </section>

        <section className="grid gap-px overflow-hidden rounded-[28px] border border-ink/[0.07] bg-ink/[0.07] sm:grid-cols-3">
          {[
            { k: total, l: "Applications waiting" },
            { k: "60s", l: "Recorded pitch per candidate" },
            { k: job.setting || job.contract || "—", l: "Role type" },
          ].map((s) => (
            <div key={s.l} className="bg-white px-7 py-8">
              <p className="font-serif text-[32px] leading-none tracking-[-0.03em] text-purple-strong">{s.k}</p>
              <p className={`mt-3 ${label}`}>{s.l}</p>
            </div>
          ))}
        </section>

        <section className="rounded-2xl border border-ink/[0.07] bg-white shadow-[0_1px_2px_rgba(13,27,46,0.04),0_18px_44px_-28px_rgba(13,27,46,0.22)]">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-ink/10 px-6 py-5">
            <div className="min-w-0">
              <h2 className="font-serif text-[18px] tracking-[-0.01em] text-ink">{job.title}</h2>
              <p className="mt-1.5 text-[14px] text-ink/55">
                {job.company} · {job.location} · {job.setting} · {job.contract}
              </p>
              <p className="mt-1 text-[14px] font-semibold text-ink/75">{job.pay}</p>
              {job.advert ? (
                <p className="mt-3 max-w-[560px] text-[14px] leading-[1.6] text-ink/55">
                  {job.advert.length > 220 ? `${job.advert.slice(0, 220).trimEnd()}...` : job.advert}
                </p>
              ) : null}
            </div>
            <div className="text-right">
              <p className="font-serif text-[21px] leading-none text-purple-strong">{total}</p>
              <p className={`mt-1.5 ${label}`}>Applicant{total === 1 ? "" : "s"}</p>
            </div>
          </div>

          {candidates.length === 0 ? (
            <p className="px-6 py-10 text-center text-[15px] text-ink/50">
              No applications on this role yet. We will email you as soon as the first pitches land.
            </p>
          ) : (
            <div className="divide-y divide-ink/10">
              {candidates.map((c) => (
                <TeaserRow key={c.id} candidate={c} />
              ))}
            </div>
          )}
        </section>

        <section className="rounded-[28px] border border-ink/[0.07] bg-white px-6 py-10 sm:px-10">
          <p className={label}>How this works</p>
          <h2 className="mt-3 max-w-[720px] font-serif text-[22px] leading-[1.05] tracking-[-0.025em] text-ink sm:text-[32px]">
            Three steps between you and your next closer.
          </h2>
          <div className="mt-9 grid gap-8 sm:grid-cols-3">
            {[
              { n: "01", t: "Create your free account", d: "No card, no contract. It takes under a minute and your role is already loaded." },
              { n: "02", t: "Listen to the pitches", d: "Every applicant recorded a 60-second pitch and answered three sales questions. Always free." },
              { n: "03", t: "Unlock who you want", d: "Open full profiles, CVs and contact details for the role you want to hire on." },
            ].map((s) => (
              <div key={s.n} className="border-t border-purple-strong/20 pt-5">
                <p className="text-[12px] font-bold uppercase tracking-[0.28em] text-purple-mid">{s.n}</p>
                <h3 className="mt-3 font-serif text-[18px] leading-[1.15] tracking-[-0.02em] text-ink">{s.t}</h3>
                <p className="mt-2.5 text-[15px] leading-[1.65] text-ink/60">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="relative overflow-hidden rounded-[36px] bg-purple-strong px-6 py-16 sm:px-12 sm:py-20">
          <div className="pointer-events-none absolute -left-32 -top-32 size-[480px] rounded-full bg-[radial-gradient(circle,rgba(230,238,251,0.2),transparent_65%)] blur-2xl" />
          <div className="pointer-events-none absolute -bottom-40 right-0 size-[520px] rounded-full bg-[radial-gradient(circle,rgba(235,225,253,0.18),transparent_65%)] blur-2xl" />
          <div className="relative mx-auto w-full max-w-[760px] text-center">
            <p className="text-[12px] font-bold uppercase tracking-[0.28em] text-mint-soft">Your candidates are waiting</p>
            <h2 className="mt-6 font-serif text-[28px] leading-[0.98] tracking-[-0.03em] text-white sm:text-[40px]">
              We&apos;ve already done the hard part.
            </h2>
            <p className="mx-auto mt-6 max-w-[620px] text-[17px] leading-[1.7] text-white/65">
              We brought salespeople to your role and we&apos;re still bringing more. Create your free account to see
              who applied, hear them sell, and find the people worth your time.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link {...claimHref} className="inline-flex items-center rounded-full bg-white px-8 py-4 text-[13.5px] font-bold uppercase tracking-[0.1em] text-purple-strong transition-colors hover:bg-mint-soft">
                Create free account
              </Link>
            </div>
            <p className="mt-6 text-[12px] font-bold uppercase tracking-[0.2em] text-white/45">
              Questions? info@closdd.com
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}

function TeaserRow({ candidate }: { candidate: PublicPreviewCandidate }) {
  return (
    <div className="grid gap-5 px-6 py-5 sm:grid-cols-[1fr_auto] sm:items-start lg:grid-cols-[minmax(260px,1fr)_400px] lg:items-center">
      <div className="flex min-w-0 items-center gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-purple-soft text-[15px] font-bold text-purple-strong">
          {initialsOf(candidate.firstName)}
        </span>
        <div className="min-w-0">
          <p className="text-[17px] font-semibold text-ink">{candidate.firstName}</p>
          <p className="mt-0.5 text-[13px] text-ink/50">
            {[candidate.years, candidate.location, candidate.appliedAt].filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>

      {candidate.audioUrl ? (
        <audio src={candidate.audioUrl} controls className="w-full" />
      ) : (
        <p className="rounded-xl border border-ink/10 bg-[#fbfcfe] px-4 py-3.5 text-[13px] text-ink/50">
          No pitch recorded yet.
        </p>
      )}
    </div>
  );
}
